// Served from public/ so the URLs are stable in dev/build and directly testable
// (new Audio(this).play()). BASE_URL keeps them correct under a non-root base.
const CHATWOOT_NOTIFICATION_SOUND = `${import.meta.env.BASE_URL}sounds/chatwoot-notification.mp3`
const PULSE_APPROVAL_SOUND = `${import.meta.env.BASE_URL}sounds/pulse-approval.mp3`

// Both notification sounds sit at the same level so neither surprises an
// operator who has the inbox and Pulse open at once.
const NOTIFICATION_VOLUME = 0.5

// Long enough to swallow a burst without stuttering, short enough that two
// genuinely separate events a couple of seconds apart are both heard.
const THROTTLE_MS = 1500

// One reused element per sound, rewound before each play so back-to-back events
// retrigger it. Autoplay policy: play() rejects until the operator has
// interacted with the page, which is swallowed — sound is progressive
// enhancement and must never break the page that called it.
//
// Deliberately an HTMLAudioElement and not Web Audio. Oscillators are scheduled
// against AudioContext.currentTime, which stops advancing while a backgrounded
// tab's context is suspended, so every alert raised while the operator was
// looking elsewhere queued up at the same frozen timestamp and then fired
// together on their next click — one summed, clipping burst instead of the
// alerts they had missed. play() resolves or rejects immediately, so nothing
// accumulates.
function createSound(url: string) {
  let element: HTMLAudioElement | null = null
  let lastPlayedAt = 0

  return function play(): void {
    try {
      if (typeof Audio === 'undefined') {
        return
      }
      const now = Date.now()
      if (now - lastPlayedAt < THROTTLE_MS) {
        return
      }
      lastPlayedAt = now
      if (!element) {
        element = new Audio(url)
        element.volume = NOTIFICATION_VOLUME
      }
      element.currentTime = 0
      void element.play().catch(() => undefined)
    } catch {
      // Sound is optional; never let it break the caller.
    }
  }
}

// Pulse needs an operator to approve or review a blocked run. Only ever called
// behind claimApprovalAlert() so that an operator with several windows open
// hears this once, not once per window.
export const playApprovalAlert = createSound(PULSE_APPROVAL_SOUND)

// The operator inbox received a customer message. Only wired into the operator
// inbox — customers never call this.
export const playChatwootNotification = createSound(CHATWOOT_NOTIFICATION_SOUND)
